import { useState, useEffect } from 'react';
import { FileWarning, Clock, Calendar, Info, AlertCircle } from '../../icons/wp-icons';
import apiClient from '../../api/apiClient';

function MahnwesenStep({ data = {}, onDataChange }) {
  const [mahnlaufAktiv, setMahnlaufAktiv] = useState(data.mahnlaufAktiv ?? false);
  const [mahnlaufIntervall, setMahnlaufIntervall] = useState(data.mahnlaufIntervall || 'taeglich');
  const [mahnlaufUhrzeit, setMahnlaufUhrzeit] = useState(data.mahnlaufUhrzeit || '09:00');
  const [mahnlaufWochentag, setMahnlaufWochentag] = useState(data.mahnlaufWochentag || 1);

  const [stufe1Tage, setStufe1Tage] = useState(data.stufe1Tage || 7);
  const [stufe2Tage, setStufe2Tage] = useState(data.stufe2Tage || 14);
  const [stufe3Tage, setStufe3Tage] = useState(data.stufe3Tage || 28);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Lade bestehende Einstellungen
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await apiClient.get('/mahnungen/einstellungen');
        const settings = response.data;

        setMahnlaufAktiv(settings.mahnlaufAktiv || false);
        setMahnlaufIntervall(settings.mahnlaufIntervall || 'taeglich');
        setMahnlaufUhrzeit(settings.mahnlaufUhrzeit || '09:00');
        setMahnlaufWochentag(settings.mahnlaufWochentag || 1);
        setStufe1Tage(settings.stufe1Tage || 7);
        setStufe2Tage(settings.stufe2Tage || 14);
        setStufe3Tage(settings.stufe3Tage || 28);
      } catch (err) {
        console.error('Fehler beim Laden der Mahnungseinstellungen:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Daten an Parent weitergeben wenn sich etwas aendert
  useEffect(() => {
    const newData = {
      mahnlaufAktiv,
      mahnlaufIntervall,
      mahnlaufUhrzeit,
      mahnlaufWochentag,
      stufe1Tage,
      stufe2Tage,
      stufe3Tage
    };
    // Dieser Schritt ist immer valid (optional)
    onDataChange(newData, true);
  }, [mahnlaufAktiv, mahnlaufIntervall, mahnlaufUhrzeit, mahnlaufWochentag, stufe1Tage, stufe2Tage, stufe3Tage]);

  // Speichere Einstellungen wenn sich etwas aendert
  useEffect(() => {
    if (loading) return;

    const saveSettings = async () => {
      try {
        await apiClient.post('/mahnungen/einstellungen', {
          mahnlaufAktiv,
          mahnlaufIntervall,
          mahnlaufUhrzeit,
          mahnlaufWochentag,
          stufe1Tage,
          stufe2Tage,
          stufe3Tage
        });
        setError('');
      } catch (err) {
        console.error('Fehler beim Speichern:', err);
        setError('Fehler beim Speichern der Einstellungen');
      }
    };

    // Debounce das Speichern
    const timeout = setTimeout(saveSettings, 500);
    return () => clearTimeout(timeout);
  }, [mahnlaufAktiv, mahnlaufIntervall, mahnlaufUhrzeit, mahnlaufWochentag, stufe1Tage, stufe2Tage, stufe3Tage, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#378ab2]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Automatisches Mahnwesen
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Richten Sie automatische Mahnungen fuer ueberfaellige Rechnungen ein.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Aktivierung */}
      <div className="p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FileWarning className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-900">
                Automatischer Mahnlauf
              </label>
              <p className="text-sm text-gray-500">
                Mahnungen werden automatisch per E-Mail versendet
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={mahnlaufAktiv}
              onChange={(e) => setMahnlaufAktiv(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#378ab2]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#378ab2]"></div>
          </label>
        </div>
      </div>

      {/* Mahnstufen */}
      <div className="p-4 bg-gray-50 rounded-xl space-y-4">
        <h4 className="font-medium flex items-center gap-2 text-gray-900">
          <Info className="w-4 h-4" /> Mahnstufen-Konfiguration
        </h4>
        <p className="text-sm text-gray-500">
          Nach wie vielen Tagen ab Faelligkeit soll die jeweilige Mahnung versendet werden?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center justify-between sm:flex-col sm:items-start gap-2">
            <label className="text-sm font-medium text-gray-700">
              1. Mahnung
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="365"
                value={stufe1Tage}
                onChange={(e) => setStufe1Tage(parseInt(e.target.value) || 7)}
                className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] text-sm text-center"
              />
              <span className="text-sm text-gray-500">Tage</span>
            </div>
          </div>
          <div className="flex items-center justify-between sm:flex-col sm:items-start gap-2">
            <label className="text-sm font-medium text-gray-700">
              2. Mahnung
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="365"
                value={stufe2Tage}
                onChange={(e) => setStufe2Tage(parseInt(e.target.value) || 14)}
                className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] text-sm text-center"
              />
              <span className="text-sm text-gray-500">Tage</span>
            </div>
          </div>
          <div className="flex items-center justify-between sm:flex-col sm:items-start gap-2">
            <label className="text-sm font-medium text-gray-700">
              3. Mahnung
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="365"
                value={stufe3Tage}
                onChange={(e) => setStufe3Tage(parseInt(e.target.value) || 28)}
                className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] text-sm text-center"
              />
              <span className="text-sm text-gray-500">Tage</span>
            </div>
          </div>
        </div>
      </div>

      {/* Zeitplan */}
      {mahnlaufAktiv && (
        <div className="p-4 bg-gray-50 rounded-xl space-y-4">
          <h4 className="font-medium flex items-center gap-2 text-gray-900">
            <Clock className="w-4 h-4" /> Zeitplan
          </h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intervall
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMahnlaufIntervall('taeglich')}
                className={`px-4 py-2 rounded-button border-2 transition ${
                  mahnlaufIntervall === 'taeglich'
                    ? 'border-[#378ab2] bg-[#378ab2]/10 text-[#378ab2]'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                Taeglich
              </button>
              <button
                type="button"
                onClick={() => setMahnlaufIntervall('woechentlich')}
                className={`px-4 py-2 rounded-button border-2 transition ${
                  mahnlaufIntervall === 'woechentlich'
                    ? 'border-[#378ab2] bg-[#378ab2]/10 text-[#378ab2]'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                Woechentlich
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Uhrzeit
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="time"
                  value={mahnlaufUhrzeit}
                  onChange={(e) => setMahnlaufUhrzeit(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] text-sm"
                />
              </div>
            </div>

            {mahnlaufIntervall === 'woechentlich' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Wochentag
                </label>
                <select
                  value={mahnlaufWochentag}
                  onChange={(e) => setMahnlaufWochentag(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] text-sm"
                >
                  <option value={1}>Montag</option>
                  <option value={2}>Dienstag</option>
                  <option value={3}>Mittwoch</option>
                  <option value={4}>Donnerstag</option>
                  <option value={5}>Freitag</option>
                  <option value={6}>Samstag</option>
                  <option value={0}>Sonntag</option>
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Hinweis:</strong> Dieser Schritt ist optional. Sie koennen die Mahnungseinstellungen
          auch spaeter in den Einstellungen anpassen. Das E-Mail-Template fuer Mahnungen koennen Sie
          ebenfalls dort konfigurieren.
        </div>
      </div>
    </div>
  );
}

export default MahnwesenStep;
